import { useEffect, useMemo, useState, useCallback } from "react";
import { CheckCircle2, XCircle, PackageCheck, PackageX, Truck, Loader2, AlertTriangle, Warehouse, Briefcase } from "lucide-react";
import { fetchDeliveryChecklist, markShoppingDelivered, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import IndkoebTabs from "@/components/dashboard/IndkoebTabs";
import { SpecialDeliverySection } from "@/components/dashboard/SpecialOrderSections";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function DestBadge({ item }) {
    if (item.destination_type === "stock") return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-700"><Warehouse className="w-3 h-3" /> Lager</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-100 text-blue-700"><Briefcase className="w-3 h-3" /> {item.job_label || "Opgave"}</span>;
}

function DeliveryStatusPill({ s }) {
    if (s === "awaiting") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Afventer levering</span>;
    if (s === "partially_received") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Delvist modtaget</span>;
    if (s === "not_received") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Ikke kommet</span>;
    if (s === "received") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Modtaget</span>;
    return null;
}

export default function DeliveryChecklist() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [gated, setGated] = useState(false);
    const [partial, setPartial] = useState(null);
    const [notReceived, setNotReceived] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const list = await fetchDeliveryChecklist();
            setItems(list);
            setGated(false);
        } catch (err) {
            if (err?.response?.status === 503) {
                setGated(true); setItems([]);
            } else {
                toast.error(formatApiError(err));
            }
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const filtered = useMemo(() => {
        const q = (search || "").toLowerCase().trim();
        if (!q) return items;
        return items.filter((it) => (it.item_name || "").toLowerCase().includes(q) || (it.supplier || "").toLowerCase().includes(q) || (it.job_label || "").toLowerCase().includes(q));
    }, [items, search]);

    if (gated) {
        return (
            <div className="p-5 sm:p-8 max-w-4xl mx-auto">
                <IndkoebTabs />
                <PageHeader title="Leveringscheckliste" subtitle="Native indkøbsliste er ikke aktiveret — specialbestillinger fra AI-opkald vises nedenfor." />
                <SpecialDeliverySection />
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-900">
                    <div className="flex items-center gap-2 font-semibold mb-2"><AlertTriangle className="w-5 h-5" /> Native leveringscheckliste ikke aktiveret</div>
                    <p className="text-sm">Native-leveringschecklisten er endnu ikke slået til for denne virksomhed.</p>
                </div>
            </div>
        );
    }

    const quickFullReceive = async (item) => {
        try {
            const res = await markShoppingDelivered(item.id, { mode: "fully_received" });
            const su = res?.stock_update;
            if (su) {
                toast.success(`Fuld levering registreret — lager '${su.material_name}' +${su.delta} ${su.unit} (nu ${su.new_stock})`);
            } else {
                toast.success("Fuld levering registreret");
            }
            refresh();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="delivery-checklist-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <IndkoebTabs />
            <PageHeader
                title="Leveringscheckliste"
                subtitle="Varer der er bestilt og afventer levering. Registrér ankomst — helt, delvist eller udeblevet."
                right={
                    <div className="flex flex-wrap items-center gap-3">
                        <SearchInput value={search} onChange={setSearch} placeholder="Søg vare, leverandør, opgave…" testid="delivery-search" />
                    </div>
                }
            />

            <SpecialDeliverySection />

            <div className="mt-5 space-y-2">
                {loading && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Indlæser…</div>}
                {!loading && filtered.length === 0 && (
                    <div className="text-center py-12 text-slate-500 bg-white border border-slate-200 rounded-xl">
                        <Truck className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        Ingen varer venter på levering lige nu.
                    </div>
                )}
                {filtered.map((item) => (
                    <div key={item.id} data-testid={`delivery-row-${item.id}`} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-blue-200">
                        <Truck className="w-9 h-9 p-2 text-indigo-600 bg-indigo-50 rounded-lg shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-900 truncate">{item.item_name}</span>
                                <span className="text-slate-500 text-sm">· bestilt {item.quantity_ordered} {item.unit}, modtaget {item.quantity_received || 0} — mangler <strong className="text-orange-700">{item.qty_missing || 0}</strong></span>
                                <DestBadge item={item} />
                                <DeliveryStatusPill s={item.delivery_status} />
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                                {item.supplier && <span>Leverandør: {item.supplier}</span>}
                                {item.expected_delivery_at && <span>· Forventet: {item.expected_delivery_at}</span>}
                                {item.received_by?.name && <span>· Senest opdateret af: {item.received_by.name}</span>}
                                {item.ordered_by?.name && !item.received_by?.name && <span>· Bestilt af: {item.ordered_by.name}</span>}
                            </div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                            <Button data-testid={`delivery-full-${item.id}`} size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => quickFullReceive(item)}>
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Er der
                            </Button>
                            <Button data-testid={`delivery-partial-${item.id}`} size="sm" variant="outline" className="text-orange-700 border-orange-200 hover:bg-orange-50" onClick={() => setPartial(item)}>
                                <PackageCheck className="w-4 h-4 mr-1" /> Mangler lidt
                            </Button>
                            <Button data-testid={`delivery-none-${item.id}`} size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setNotReceived(item)}>
                                <PackageX className="w-4 h-4 mr-1" /> Ikke kommet
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {partial && <PartialDialog item={partial} onClose={() => setPartial(null)} onDone={() => { setPartial(null); refresh(); }} />}
            {notReceived && <NotReceivedDialog item={notReceived} onClose={() => setNotReceived(null)} onDone={() => { setNotReceived(null); refresh(); }} />}
        </div>
    );
}

function PartialDialog({ item, onClose, onDone }) {
    const [qty, setQty] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        const n = Number(qty);
        if (isNaN(n) || n <= 0) { toast.error("Angiv modtaget antal."); return; }
        setSaving(true);
        try {
            const res = await markShoppingDelivered(item.id, { mode: "partially_received", qty_received: n, notes: notes.trim() || undefined });
            const su = res?.stock_update;
            if (su) {
                toast.success(`Delvis levering registreret — lager '${su.material_name}' +${su.delta} ${su.unit} (nu ${su.new_stock})`);
            } else {
                toast.success("Delvis levering registreret");
            }
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-orange-700"><PackageCheck className="w-5 h-5" /> Delvis levering</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{item.item_name} · bestilt {item.quantity_ordered} {item.unit}</div>
                <div className="mt-3 space-y-3">
                    <div>
                        <Label>Hvor meget er modtaget? *</Label>
                        <Input data-testid="delivery-partial-qty" type="number" min="0" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 h-10" placeholder={`Max ${item.quantity_ordered}`} />
                    </div>
                    <div>
                        <Label>Note <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                        <textarea data-testid="delivery-partial-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="Fx: Resterende ventes fredag" />
                    </div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                    <Button type="button" disabled={saving} onClick={submit} data-testid="delivery-partial-submit" className="bg-orange-600 hover:bg-orange-700 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Registrér
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function NotReceivedDialog({ item, onClose, onDone }) {
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        setSaving(true);
        try {
            await markShoppingDelivered(item.id, { mode: "not_received", notes: notes.trim() || undefined });
            toast.success("Registreret som ikke-modtaget");
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><XCircle className="w-5 h-5" /> Ikke kommet</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{item.item_name}</div>
                <div className="mt-3">
                    <Label>Note <span className="text-slate-400 text-xs">(valgfri)</span></Label>
                    <textarea data-testid="delivery-notreceived-notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm" placeholder="Fx: Leverandøren ringede — udsat 3 dage" />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Luk</Button>
                    <Button type="button" disabled={saving} onClick={submit} data-testid="delivery-notreceived-submit" className="bg-red-600 hover:bg-red-700 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Bekræft
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
